import { Box, Button, Typography, Stack, ToggleButton, ToggleButtonGroup, Link } from '@mui/material';
import RevelataLogoSvg from '../static/RevelataIncLogo.svg';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fade from '@mui/material/Fade';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { brand, gray } from '../getTheme/getTheme';
import { signOutAndRedirect } from '../Utils/Utils';
import '@aws-amplify/ui-react/styles.css';
import { styled } from "@mui/material/styles";
import { Auth } from 'aws-amplify';
import { ReactComponent as GoogleG } from '../static/GoogleGDark.svg';
import SubscriptionNudgePopUp from '../Subscription/SubscriptionNudgePopUp';
import { useIsMobile } from '../Utils';

export const TOP_BAR_HEIGHT = 160;

// Styling for Authenticator Google Sign In button (copied from Login.jsx)
const AmplifyWrapper = styled('div')({
    '& .federated-sign-in-button': {
        backgroundColor: 'transparent !important',
        border: `1px solid ${gray[50]} !important`,
        color: `${gray[50]} !important`,
        position: 'relative',
    },
    '& .federated-sign-in-button .amplify-text': {
        color: `${gray[50]} !important`,
    },
    '& .federated-sign-in-button:hover': {
        backgroundColor: `linear-gradient(45deg, ${brand[800]} 20%, ${brand[900]} 90%)`,
        border: '1px solid transparent !important',
        borderImage: `linear-gradient(45deg, ${brand[400]} 20%, ${brand[700]} 90%) 1 !important`,
    },
    '& .federated-sign-in-button:active': {
        backgroundColor: `${brand[900]} !important`,
        borderColor: `${brand[300]} !important`,
    },
    // Suppress its error messages to the UI so we can display them ourselves
    '& .amplify-alert': {
        display: 'none !important'
    },
    '& .amplify-alert--error': {
        display: 'none !important'
    }
});

export function FreeTopAppBar({
    ALLOWED_TICKERS,
    ticker,
    selectedPane,
    handleTickerChange,
    handlePaneChange,
    expanded,
    setExpanded,
    children
}) {

    const isMobile = useIsMobile();

    const topBarHeader = "Live Demo: Weekly KPI Deep Dive";
    const topBarHeaderMobile = "Live Demo: Weekly KPI Deep Dive";
    const topBarHeaderMobileShort = "Live Demo: KPI Deep Dive";
    const topBarTextLong = <>Each week, we give free full access to three select tickers on our platform.  Use deepKPI to browse their KPI time series, tear sheets, and comps.  Then, interrogate their data, earnings calls, and filings with deepAnalysis.</>
    const topBarTextShort = <>Access all S&P 500, 400, and 600 companies, <b>no credit card required.</b></>
    const topBarCtaText = "Like what you see? Sign up for a free trial of our full platform!"

    if (isMobile) {

        return (
            <>
                <SubscriptionNudgePopUp />
                {/* Blurred backdrop that animates with the bar */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: '0px',
                        left: 0,
                        width: '100vw',
                        height: expanded ? '75vh' : `${TOP_BAR_HEIGHT}px`,
                        zIndex: 1199,
                        pointerEvents: 'none',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        transition: 'top 0.25s cubic-bezier(0.4, 0, 0.2, 1), height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
                {/*Main Bar */}
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: expanded ? '75vh' : `${TOP_BAR_HEIGHT}px`,
                        background: expanded ? 'linear-gradient(135deg, rgba(35, 50, 35, 0.9) 0%, rgba(36, 61, 82, 0.99) 100%)' :
                            'linear-gradient(135deg, rgba(35, 50, 35, 1) 0%, rgba(36, 61, 82, 1) 100%)',
                        boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.08)',
                        borderRadius: 0,
                        zIndex: 1200,
                        pointerEvents: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflow: 'hidden',
                    }}
                >

                    {/*Collapsed Bar */}
                    {!expanded && (<Fade in={!expanded} timeout={800} unmountOnExit>
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            justifyContent: 'center',
                            mt: 1,
                            mb: 0,
                            px: 1,
                            gap: 2
                        }}>

                            {/* Mobile: Logo and CTA */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                width: '100%',
                                mb: 0,
                                pl: 0,
                                mt: 0,
                                gap: 0.15,
                            }}>
                                <Box sx={{ display: 'flex', flexDirection: 'row', textAlign: 'center', alignItems: 'center', mb: 0, width: '100%' }}>
                                    <Link href="/" onClick={e => { e.preventDefault(); signOutAndRedirect('/'); }}>
                                        <img src={RevelataLogoSvg} alt="Revelata Logo" style={{ height: '30px' }} />
                                    </Link>

                                    <Typography variant="body1" color="text.primary" sx={{ mt: 0, ml: 2, fontWeight: 'bold', display: 'block' }}>
                                        {topBarHeaderMobileShort}
                                    </Typography>
                                </Box>

                                {/* Ticker selection buttons for mobile */}
                                <Stack direction="row" spacing={0.5} sx={{ mt: 0, mb: 2, justifyContent: 'center' }}>
                                    {ALLOWED_TICKERS.map((t) => (
                                        <Button
                                            key={t}
                                            onClick={() => { handleTickerChange(t); }}
                                            variant={ticker === t ? 'contained' : 'outlined'}
                                            sx={{ borderRadius: 3, fontSize: { xs: '0.65rem', md: '1rem', p: 0, m: 0 } }}>
                                            {t}
                                        </Button>
                                    ))}
                                </Stack>

                                {/* <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'left', justifyContent: 'left', mt: 0 }}> */}
                                <Button
                                    variant="outlined"

                                    onClick={async () => {
                                        try {
                                            const user = await Auth.currentAuthenticatedUser();
                                            const isDemo = user?.attributes?.email === 'free-demo-user@revelata.com' || user?.username === 'demo';
                                            if (isDemo) {
                                                await Auth.signOut();
                                            }
                                        } catch (e) {
                                            // If not authenticated, ignore
                                        }
                                        await Auth.federatedSignIn({ provider: 'Google' });
                                    }}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        px: 1,
                                        py: 0.5,
                                        mt: 0,
                                        display: 'block'
                                    }}
                                >
                                    <GoogleG style={{ height: '12px', marginRight: '8px', verticalAlign: 'middle' }} />
                                    Sign In with Google for Full Access
                                </Button>
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 0, mb: 1, display: 'block' }}>
                                    <Link href="/signup" sx={{ color: 'var(--brand-main)', textDecoration: 'none', fontSize: '0.8rem', cursor: 'pointer' }}>Or, create an account</Link>
                                </Typography>


                            </Box>

                        </Box>
                    </Fade>)}

                    {/*Expanded bar */}
                    {expanded && (
                        <ClickAwayListener onClickAway={() => setExpanded(false)}>
                            <Fade in={expanded} timeout={800}>
                                <Box sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '90vw',
                                    height: '100%',
                                    minHeight: 300,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    px: { xs: 2, md: 0 },
                                    py: 2,
                                }}>
                                    {!isMobile && (<Box sx={{ height: '20%', width: '100%' }} />)}
                                    <Link href="/"
                                        onClick={e => {
                                            e.preventDefault();
                                            signOutAndRedirect('/');
                                        }}>
                                        <img src={RevelataLogoSvg} alt="Revelata Logo" style={{ height: '40px', marginRight: '16px', marginTop: 0 }} />
                                    </Link>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', mb: 1, mt: 6 }}>
                                        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold', textAlign: 'left', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                                            {topBarHeaderMobile}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, textAlign: 'left', fontSize: { xs: '0.95rem', md: '1rem' } }}>
                                            {topBarTextLong}  {' '}
                                            <Link href="/signup" sx={{ color: 'var(--brand-main)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }} onClick={e => { e.preventDefault(); signOutAndRedirect('/signup'); }}>{topBarCtaText}</Link>
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1, mt: 2, width: '100%' }}>
                                        <Stack direction="row" spacing={2}>
                                            {ALLOWED_TICKERS.map((ticker) => (
                                                <Button
                                                    key={ticker}
                                                    onClick={() => {
                                                        handleTickerChange(ticker);
                                                        setExpanded(false);
                                                    }}
                                                    variant={'contained'}
                                                    sx={{ borderRadius: 3, fontSize: { xs: '0.85rem', md: '1rem' } }}
                                                >
                                                    {ticker}
                                                </Button>
                                            ))}
                                        </Stack>
                                    </Box>
                                    <Button
                                        variant="text"
                                        size="small"
                                        sx={{
                                            mt: 'auto',
                                            color: 'text.secondary',
                                            textTransform: 'none',
                                            alignSelf: 'center',
                                            minWidth: 180,
                                            display: 'block'
                                        }}
                                        onClick={() => setExpanded(false)}
                                    >
                                        <KeyboardArrowUpIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Collapse
                                    </Button>
                                </Box>
                            </Fade>
                        </ClickAwayListener>
                    )}
                </Box>


                {/* Spacer to push page content below the bar */}
                <Box sx={{ height: `${TOP_BAR_HEIGHT - 16}px`, width: '100%' }} />
                {/* Main content with grayscale overlay when expanded */}
                <div className={expanded ? 'grayscale-underlay' : ''}>
                    {children}
                </div>
            </>
        );



    }


    // Desktop version:
    return (
        <>
            <SubscriptionNudgePopUp />
            {/* Blurred backdrop that animates with the bar */}
            <Box
                sx={{
                    position: 'fixed',
                    top: '0px',
                    left: 0,
                    width: '100vw',
                    height: expanded ? '75vh' : `${TOP_BAR_HEIGHT}px`,
                    zIndex: 1199,
                    pointerEvents: 'none',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    transition: 'top 0.25s cubic-bezier(0.4, 0, 0.2, 1), height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            />
            {/*Main Bar */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: expanded ? '75vh' : `${TOP_BAR_HEIGHT}px`,
                    background: expanded ? 'linear-gradient(135deg, rgba(35, 50, 35, 0.9) 0%, rgba(36, 61, 82, 0.99) 100%)' :
                        'linear-gradient(135deg, rgba(35, 50, 35, 1) 0%, rgba(36, 61, 82, 1) 100%)',
                    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.08)',
                    borderRadius: 0,
                    zIndex: 1200,
                    pointerEvents: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                }}
            >

                {/*Collapsed Bar */}
                {!expanded && (<Fade in={!expanded} timeout={800} unmountOnExit>
                    <Box sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        mt: 1,
                        mb: 0,
                        px: 1,
                        gap: 2,
                    }}>


                        {/* Desktop: Logo, Title, CTA */}
                        <Box sx={{
                            width: { md: '80%' },
                            textAlign: 'left',
                            mb: 0,
                            pl: 4,
                            mt: '16px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: 0,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '30%' }}>
                                <Link href="/"
                                    onClick={e => {
                                        e.preventDefault();
                                        signOutAndRedirect('/');
                                    }}>
                                    <img src={RevelataLogoSvg} alt="Revelata Logo" style={{ height: '40px', marginRight: '16px', marginTop: 0 }} />
                                </Link>
                            </Box>


                            <Box sx={{ width: '90%' }}>
                                <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                                    {topBarHeader}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 0, mb: 1, display: 'block' }}>
                                    {topBarTextShort}  {''}
                                    <Link href="/signup" sx={{ color: 'var(--brand-main)', textDecoration: 'underline', cursor: 'pointer' }}>Create an account</Link> or
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={async () => {
                                        try {
                                            const user = await Auth.currentAuthenticatedUser();
                                            const isDemo = user?.attributes?.email === 'free-demo-user@revelata.com' || user?.username === 'demo';
                                            if (isDemo) {
                                                await Auth.signOut();
                                            }
                                        } catch (e) {
                                            // If not authenticated, ignore
                                        }
                                        await Auth.federatedSignIn({ provider: 'Google' });
                                    }}
                                    sx={{
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        fontSize: '0.9rem',
                                        px: 2,
                                        py: 1,
                                        mt: 0
                                    }}
                                >
                                    <GoogleG style={{ height: '16px', marginRight: '8px', verticalAlign: 'middle' }} />
                                    Sign In with Google for Full Access
                                </Button>

                            </Box>
                        </Box>

                        {/* Ticker and Pane Controls */}
                        <Box sx={{
                            width: '40%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            pr: 4,
                            mt: '16px',
                        }}>
                            <Stack direction="row" spacing={1}>
                                {ALLOWED_TICKERS.map((t) => (
                                    <Button
                                        key={t}
                                        onClick={() => {
                                            handleTickerChange(t);
                                        }}
                                        variant={ticker === t ? 'contained' : 'outlined'}
                                        sx={{ borderRadius: 3, fontSize: '1rem' }}
                                    >
                                        {t}
                                    </Button>
                                ))}
                            </Stack>
                            <ToggleButtonGroup
                                value={selectedPane}
                                exclusive
                                onChange={handlePaneChange}
                                aria-label="pane selector"
                            >
                                <ToggleButton
                                    value="deepKPI"
                                    aria-label="deepKPI"
                                    sx={{
                                        fontFamily: 'Figtree',
                                        textTransform: 'none',
                                        fontSize: { xs: '0.85rem', md: '1rem' },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(95, 198, 221, 0.26)',
                                            color: '#fff',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.15)'
                                            }
                                        }
                                    }}
                                >
                                    deepKPI
                                </ToggleButton>
                                <ToggleButton
                                    value="deepAnalysis"
                                    aria-label="deepAnalysis AI"
                                    sx={{
                                        fontFamily: 'Figtree',
                                        textTransform: 'none',
                                        fontSize: { xs: '0.85rem', md: '1rem' },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(95, 198, 221, 0.26)',
                                            color: '#fff',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255,255,255,0.15)'
                                            }
                                        }
                                    }}
                                >
                                    deepAnalysis AI
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                </Fade>)}

                {/*Expanded bar */}
                {expanded && (
                    <ClickAwayListener onClickAway={() => setExpanded(false)}>
                        <Fade in={expanded} timeout={800}>
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '40%',
                                height: '100%',
                                minHeight: 300,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                px: 0,
                                py: 2,
                            }}>
                                {!isMobile && (<Box sx={{ height: '20%', width: '100%' }} />)}
                                <Link href="/"
                                    onClick={e => {
                                        e.preventDefault();
                                        signOutAndRedirect('/');
                                    }}>
                                    <img src={RevelataLogoSvg} alt="Revelata Logo" style={{ height: '40px', marginRight: '16px', marginTop: 0 }} />
                                </Link>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', mb: 1, mt: 6 }}>
                                    <Typography variant="h6" color="text.primary" sx={{ fontWeight: 'bold', textAlign: 'left', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                                        {topBarHeader}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, textAlign: 'left', fontSize: { xs: '0.95rem', md: '1rem' } }}>
                                        {topBarTextLong}  {' '}
                                        <Link href="/signup" sx={{ color: 'var(--brand-main)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }} onClick={e => { e.preventDefault(); signOutAndRedirect('/signup'); }}>{topBarCtaText}</Link>
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1, mt: 2, width: '100%' }}>
                                    <Stack direction="row" spacing={2}>
                                        {ALLOWED_TICKERS.map((ticker) => (
                                            <Button
                                                key={ticker}
                                                onClick={() => {
                                                    handleTickerChange(ticker);
                                                    setExpanded(false);
                                                }}
                                                variant={'contained'}
                                                sx={{ borderRadius: 3, fontSize: '1rem' }}
                                            >
                                                {ticker}
                                            </Button>
                                        ))}
                                    </Stack>
                                </Box>
                                <Button
                                    variant="text"
                                    size="small"
                                    sx={{
                                        mt: 'auto',
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        alignSelf: 'center',
                                        minWidth: 180,
                                        display: 'block'
                                    }}
                                    onClick={() => setExpanded(false)}
                                >
                                    <KeyboardArrowUpIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Collapse
                                </Button>
                            </Box>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Box>


            {/* Spacer to push page content below the bar */}
            <Box sx={{ height: `${TOP_BAR_HEIGHT + 10}px`, width: '100%' }} />
            {/* Main content with grayscale overlay when expanded */}
            <div className={expanded ? 'grayscale-underlay' : ''}>
                {children}
            </div>
        </>
    );
}

export default FreeTopAppBar; 